import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type { ActionButton, ViewModeOption } from 'components/modals';
import {
  ActionMenu, type ActionMenuItem, SyutGrid, type SyutGridColumn,
  SyutSelect, type SelectOption, SyutTreeGrid, type TreeGridColumn,
  ColumnSelector, useColumnVisibility, type ColumnDefinition,
} from 'components/ui';
import {
  EnterpriseScreenShell, useStandardConfirms, actionButtonFactory, useCan,
} from 'components/enterprise-shell';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { useCoaLedgers } from 'hooks/api/useAccounts';
import { accountTypeService, accountSubtypeService, coaGroupService, coaLedgerService } from 'api/services';
import type { CoaLedgerDto, CoaLedgerQueryParams } from 'api/types';
import { AccountLedgerFormModal, AccountLedgerViewModal } from '../../../modals/modules/accounts/AccountLedgerFormModal';

// ============================================================================
// SCREEN
// ============================================================================

type ViewMode = 'list' | 'grid' | 'tree';

const STRUCTURAL_COLUMN_IDS = ['select', 'actions'];

const COLUMN_DEFS: ColumnDefinition[] = [
  { id: 'select', label: '', required: true },
  { id: 'ledgerCode', label: 'Code' },
  { id: 'ledgerName', label: 'Ledger' },
  { id: 'subtypeName', label: 'Subtype' },
  { id: 'accountTypeName', label: 'Type' },
  { id: 'groupName', label: 'Group' },
  { id: 'ledgerNature', label: 'Nature' },
  { id: 'currencyCode', label: 'Currency' },
  { id: 'ledgerKind', label: 'Kind' },
  { id: 'status', label: 'Status' },
  { id: 'actions', label: 'Actions', required: true },
];

/** Flat node for the COA tree (Type → Subtype → Group → Ledger). */
interface LedgerTreeNode {
  id: string;
  parentId: string | null;
  name: string;
  kind: 'type' | 'subtype' | 'group' | 'ledger';
  nature?: string;
  currency?: string;
  statusName?: string | null;
  statusColor?: string | null;
  ledger?: CoaLedgerDto;
}

function ledgerToNode(l: CoaLedgerDto, parentId: string): LedgerTreeNode {
  return {
    id: l.id, parentId, name: `${l.ledgerCode} - ${l.ledgerName}`, kind: 'ledger',
    nature: l.ledgerNature, currency: l.currencyCode,
    statusName: l.statusName, statusColor: l.statusColor, ledger: l,
  };
}

interface LedgerAccountsScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
}

const LedgerAccountsScreen: React.FC<LedgerAccountsScreenProps> = ({ moduleIcon, tabId, moduleName }) => {
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();
  const can = useCan('ledger-accounts');
  const { confirmDelete, ConfirmDialogPortal } = useStandardConfirms();

  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const { visibleColumns, onApply: applyColumns, onReset: resetColumns } =
    useColumnVisibility('ledger-accounts-columns', COLUMN_DEFS);

  const [queryParams, setQueryParams] = useState<CoaLedgerQueryParams>({ page: 1, pageSize: 25 });
  const { data, loading, pagination, refetch } = useCoaLedgers(queryParams);

  // Tree view: COA hierarchy, lazy-loaded per node
  const [treeNodes, setTreeNodes] = useState<LedgerTreeNode[]>([]);
  const [treeLoading, setTreeLoading] = useState(false);
  const [childrenLoadedIds, setChildrenLoadedIds] = useState<Set<string>>(new Set());
  const [loadingNodeIds, setLoadingNodeIds] = useState<Set<string>>(new Set());

  const loadTreeRoots = useCallback(async () => {
    setTreeLoading(true);
    try {
      const res = await accountTypeService.getAll({ pageSize: 50 });
      setTreeNodes(res.data.map(t => ({
        id: `type-${t.id}`, parentId: null, name: t.typeName, kind: 'type' as const,
      })));
      setChildrenLoadedIds(new Set());
      setLoadingNodeIds(new Set());
    } catch {
      setTreeNodes([]);
    } finally {
      setTreeLoading(false);
    }
  }, []);

  const handleLoadChildren = useCallback(async (rowId: string, node: LedgerTreeNode) => {
    setLoadingNodeIds(prev => new Set(prev).add(rowId));
    try {
      let children: LedgerTreeNode[] = [];
      if (node.kind === 'type') {
        const realId = rowId.replace('type-', '');
        const res = await accountSubtypeService.getAll({ accountTypeId: realId, pageSize: 200 });
        children = res.data.map(s => ({
          id: `sub-${s.id}`, parentId: rowId, name: s.subtypeName, kind: 'subtype' as const,
        }));
      } else if (node.kind === 'subtype') {
        const realId = rowId.replace('sub-', '');
        const [grpRes, ledRes] = await Promise.all([
          coaGroupService.getAll({ accountSubtypeId: realId, pageSize: 500 }),
          coaLedgerService.getAll({
            accountSubtypeId: realId, ledgerKind: 'GL', pageSize: 1000, includeInactive: true,
          }),
        ]);
        const groups: LedgerTreeNode[] = grpRes.data
          .filter(g => !g.parentGroupId)
          .map(g => ({
            id: `grp-${g.id}`, parentId: rowId,
            name: `${g.groupCode} - ${g.groupName}`, kind: 'group' as const,
          }));
        const directLedgers = ledRes.data.filter(l => !l.coaGroupId).map(l => ledgerToNode(l, rowId));
        children = [...groups, ...directLedgers];
      } else if (node.kind === 'group') {
        const realId = rowId.replace('grp-', '');
        const [subGrpRes, ledRes] = await Promise.all([
          coaGroupService.getAll({ parentGroupId: realId, pageSize: 500 }),
          coaLedgerService.getAll({
            coaGroupId: realId, ledgerKind: 'GL', pageSize: 1000, includeInactive: true,
          }),
        ]);
        const childGroups: LedgerTreeNode[] = subGrpRes.data.map(g => ({
          id: `grp-${g.id}`, parentId: rowId,
          name: `${g.groupCode} - ${g.groupName}`, kind: 'group' as const,
        }));
        const ledgers = ledRes.data.map(l => ledgerToNode(l, rowId));
        children = [...childGroups, ...ledgers];
      }
      setTreeNodes(prev => [...prev, ...children]);
      setChildrenLoadedIds(prev => new Set(prev).add(rowId));
    } catch {
      notificationService.toast.error('Could not load tree nodes.');
    } finally {
      setLoadingNodeIds(prev => { const n = new Set(prev); n.delete(rowId); return n; });
    }
  }, []);

  const isTreeExpandable = useCallback((node: LedgerTreeNode) => node.kind !== 'ledger', []);

  const [searchQuery, setSearchQuery] = useState('');
  useEffect(() => {
    const t = setTimeout(
      () => setQueryParams(p => ({ ...p, searchTerm: searchQuery || undefined, page: 1 })),
      400,
    );
    return () => clearTimeout(t);
  }, [searchQuery]);

  useEffect(() => { if (viewMode === 'tree') loadTreeRoots(); }, [viewMode, loadTreeRoots]);

  const [filterOpen, setFilterOpen] = useState(false);
  const [typeFilter, setTypeFilter] = useState('');
  const [subtypeFilter, setSubtypeFilter] = useState('');
  const [kindFilter, setKindFilter] = useState('');
  const [natureFilter, setNatureFilter] = useState('');
  const [ledgerCodeFilter, setLedgerCodeFilter] = useState('');
  const [ledgerNameFilter, setLedgerNameFilter] = useState('');
  const [currencyFilter, setCurrencyFilter] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);

  const [formModalOpen, setFormModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<CoaLedgerDto | null>(null);
  const [formMode, setFormMode] = useState<'create' | 'edit' | 'copy'>('create');

  const [createInstanceId, setCreateInstanceId] = useState<string>('1');
  const [copyInstanceId, setCopyInstanceId] = useState<string>('1');
  const formModalMinimizeRef = useRef<(() => void) | null>(null);

  const handlePageChange = useCallback((idx: number) => setQueryParams(p => ({ ...p, page: idx + 1 })), []);
  const handlePageSizeChange = useCallback((size: number) => setQueryParams(p => ({ ...p, pageSize: size, page: 1 })), []);

  const handleAddNew = useCallback(() => {
    let next = 1;
    if (tabId) {
      while (isMinimized(`${tabId}-form-modal-create-${next}`)) next++;
    }
    setCreateInstanceId(String(next));
    setFormMode('create'); setSelectedRecord(null); setFormModalOpen(true);
  }, [tabId, isMinimized]);

  const handleView = useCallback((id: string) => {
    const record = data.find(l => l.id === id);
    if (!record) return;
    const viewId = tabId ? `${tabId}-view-modal-${record.ledgerCode}` : null;
    if (viewId && isMinimized(viewId)) {
      const modal = getMinimizedModal(viewId);
      dismissMinimized(viewId); modal?.onRestore?.();
      return;
    }
    setSelectedRecord(record); setViewModalOpen(true);
  }, [data, tabId, isMinimized, dismissMinimized, getMinimizedModal]);

  const handleEdit = useCallback((id: string) => {
    const record = data.find(l => l.id === id);
    if (!record) return;
    const modalId = tabId ? `${tabId}-form-modal-edit-${record.ledgerCode}` : null;
    if (modalId && isMinimized(modalId)) {
      const modal = getMinimizedModal(modalId);
      dismissMinimized(modalId); modal?.onRestore?.();
      return;
    }
    setFormMode('edit'); setSelectedRecord(record); setFormModalOpen(true);
  }, [data, tabId, isMinimized, dismissMinimized, getMinimizedModal]);

  const handleCopy = useCallback((id: string) => {
    const record = data.find(l => l.id === id);
    if (!record) return;
    const modalId = tabId ? `${tabId}-form-modal-copy-${record.ledgerCode}` : null;
    if (modalId && isMinimized(modalId)) {
      const modal = getMinimizedModal(modalId);
      dismissMinimized(modalId); modal?.onRestore?.();
      return;
    }
    if (formModalOpen) formModalMinimizeRef.current?.();
    setCopyInstanceId(record.ledgerCode);
    setFormMode('copy'); setSelectedRecord(record); setFormModalOpen(true);
  }, [data, tabId, isMinimized, dismissMinimized, getMinimizedModal, formModalOpen]);

  const handleDelete = useCallback(async (id: string) => {
    const record = data.find(l => l.id === id);
    if (!record) return;
    const ok = await confirmDelete({ resourceLabel: record.ledgerName });
    if (!ok) return;
    try {
      await coaLedgerService.delete(record.id, record.rowVersion);
      notificationService.toast.success(`Ledger "${record.ledgerName}" deleted.`);
      refetch();
      if (viewMode === 'tree') loadTreeRoots();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'An error occurred while deleting.');
    }
  }, [data, confirmDelete, refetch, viewMode, loadTreeRoots]);

  const handleSaveSuccess = useCallback(() => {
    setFormModalOpen(false); setSelectedRecord(null); refetch();
    if (viewMode === 'tree') loadTreeRoots();
  }, [refetch, viewMode, loadTreeRoots]);

  const handleRestoreModal = useCallback(
    (restoredMode: 'create' | 'edit' | 'copy', instanceId?: string, ledgerCode?: string) => {
      if (instanceId) {
        if (restoredMode === 'copy') setCopyInstanceId(instanceId);
        else setCreateInstanceId(instanceId);
      }
      if (ledgerCode) {
        const rec = data.find(l => l.ledgerCode === ledgerCode);
        if (rec) setSelectedRecord(rec);
      }
      setFormMode(restoredMode); setFormModalOpen(true);
    },
    [data],
  );

  const handleRestoreView = useCallback((ledgerCode: string) => {
    const rec = data.find(l => l.ledgerCode === ledgerCode);
    if (rec) { setSelectedRecord(rec); setViewModalOpen(true); }
  }, [data]);

  const tableColumns: SyutGridColumn<CoaLedgerDto>[] = useMemo(() => [
    { id: 'ledgerCode', header: 'Code', accessorKey: 'ledgerCode', size: 110 },
    { id: 'ledgerName', header: 'Ledger', accessorKey: 'ledgerName', size: 220 },
    { id: 'subtypeName', header: 'Subtype', accessorKey: 'subtypeName', size: 160 },
    { id: 'accountTypeName', header: 'Type', accessorKey: 'accountTypeName', size: 120 },
    {
      id: 'groupName', header: 'Group', accessorKey: 'groupName', size: 150,
      enableSorting: false, cell: ({ getValue }) => (getValue() as string) || '—',
    },
    { id: 'ledgerNature', header: 'Nature', accessorKey: 'ledgerNature', size: 120 },
    { id: 'currencyCode', header: 'Currency', accessorKey: 'currencyCode', size: 90 },
    {
      id: 'ledgerKind', header: 'Kind', accessorKey: 'ledgerKind', size: 90,
      cell: ({ row }) => {
        const r = row.original as CoaLedgerDto;
        return <span className="badge">{r.ledgerKind === 'PARTY' ? 'Party' : 'GL'}</span>;
      },
    },
    {
      id: 'status', header: 'Status', accessorKey: 'statusName', size: 110,
      cell: ({ row }) => {
        const r = row.original as CoaLedgerDto;
        return (
          <span className="badge"
            style={r.statusColor ? { background: r.statusColor, color: '#fff' } : undefined}>
            {r.statusName || 'Active'}
          </span>
        );
      },
    },
  ], []);

  const getRowActions = useCallback((rec: CoaLedgerDto): ActionMenuItem[] => {
    const items: ActionMenuItem[] = [
      { id: 'view', label: 'View Details', icon: <IconSystem.eye size={18} />, onClick: () => handleView(rec.id) },
    ];
    if (can('UPDATE')) {
      items.push({ id: 'edit', label: 'Edit', icon: <IconSystem.edit size={18} />, onClick: () => handleEdit(rec.id) });
      items.push({ id: 'copy', label: 'Copy', icon: <IconSystem.copy size={18} />, onClick: () => handleCopy(rec.id) });
    }
    if (can('DELETE')) {
      items.push({ id: 'delete', label: 'Delete', icon: <IconSystem.trash size={18} />, onClick: () => handleDelete(rec.id), variant: 'danger' });
    }
    return items;
  }, [can, handleView, handleEdit, handleCopy, handleDelete]);

  const viewModeOptions: ViewModeOption[] = [
    { id: 'list', icon: <IconSystem.list size={16} />, label: 'List View', tooltip: 'List View' },
    { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid View', tooltip: 'Grid View' },
    { id: 'tree', icon: <IconSystem.folder size={16} />, label: 'Tree View', tooltip: 'Chart of Accounts tree' },
  ];

  const treeColumns: TreeGridColumn<LedgerTreeNode>[] = useMemo(() => [
    { id: 'name', header: 'Account', accessorKey: 'name', isTreeColumn: true, size: 380 },
    {
      id: 'nature', header: 'Nature', accessorKey: 'nature', size: 120,
      cell: ({ row }) => row.original.kind === 'ledger' ? (row.original.nature || '') : '',
    },
    {
      id: 'currency', header: 'Currency', accessorKey: 'currency', size: 90,
      cell: ({ row }) => row.original.kind === 'ledger' ? (row.original.currency || '') : '',
    },
    {
      id: 'status', header: 'Status', accessorKey: 'statusName', size: 110,
      cell: ({ row }) => {
        const n = row.original;
        if (n.kind !== 'ledger') return '';
        return (
          <span className="badge"
            style={n.statusColor ? { background: n.statusColor, color: '#fff' } : undefined}>
            {n.statusName || 'Active'}
          </span>
        );
      },
    },
  ], []);

  const treeRowActions = useCallback((node: LedgerTreeNode): ActionMenuItem[] =>
    node.kind === 'ledger' && node.ledger ? getRowActions(node.ledger) : [], [getRowActions]);

  const actionButtons: ActionButton[] = [
    { ...actionButtonFactory.add(handleAddNew, 'Add Ledger Account'), visible: can('CREATE') },
    actionButtonFactory.refresh(refetch),
  ];

  const loadTypeOptions = useCallback(async (input: string): Promise<SelectOption[]> => {
    try {
      const res = await accountTypeService.getAll({ searchTerm: input || undefined, pageSize: 50 });
      return [{ value: '', label: 'All Types' }, ...res.data.map(t => ({ value: t.id, label: t.typeName }))];
    } catch {
      return [{ value: '', label: 'All Types' }];
    }
  }, []);
  const loadSubtypeOptions = useCallback(async (input: string): Promise<SelectOption[]> => {
    try {
      const res = await accountSubtypeService.getAll({
        accountTypeId: typeFilter || undefined, searchTerm: input || undefined, pageSize: 100,
      });
      return [{ value: '', label: 'All Subtypes' }, ...res.data.map(s => ({ value: s.id, label: s.subtypeName }))];
    } catch {
      return [{ value: '', label: 'All Subtypes' }];
    }
  }, [typeFilter]);
  const loadNatureOptions = useCallback(async (input: string): Promise<SelectOption[]> => {
    try {
      const natures = await coaLedgerService.getNatures(true);   // a filter finds system-set ledgers too
      const filtered = input
        ? natures.filter(n => n.label.toLowerCase().includes(input.toLowerCase()))
        : natures;
      return [{ value: '', label: 'All Natures' }, ...filtered.map(n => ({ value: n.value, label: n.label }))];
    } catch {
      return [{ value: '', label: 'All Natures' }];
    }
  }, []);

  const applyFilters = useCallback(() => {
    setQueryParams(prev => ({
      ...prev,
      accountTypeId: typeFilter || undefined,
      accountSubtypeId: subtypeFilter || undefined,
      ledgerKind: kindFilter || undefined,
      ledgerNature: natureFilter || undefined,
      ledgerCode: ledgerCodeFilter || undefined,
      ledgerName: ledgerNameFilter || undefined,
      currencyCode: currencyFilter || undefined,
      includeInactive: includeInactive || undefined,
      page: 1,
    }));
    setFilterOpen(false);
  }, [typeFilter, subtypeFilter, kindFilter, natureFilter, ledgerCodeFilter, ledgerNameFilter, currencyFilter, includeInactive]);

  const clearFilters = useCallback(() => {
    setTypeFilter(''); setSubtypeFilter(''); setKindFilter(''); setNatureFilter('');
    setLedgerCodeFilter(''); setLedgerNameFilter(''); setCurrencyFilter(''); setIncludeInactive(false);
    setQueryParams(prev => ({ page: 1, pageSize: prev.pageSize }));
  }, []);

  const appliedCount = [
    typeFilter, subtypeFilter, kindFilter, natureFilter,
    ledgerCodeFilter, ledgerNameFilter, currencyFilter, includeInactive ? '1' : '',
  ].filter(Boolean).length;

  return (
    <EnterpriseScreenShell
      title="Ledger Accounts"
      description="The GL leaf accounts — structural ledgers and (auto-provisioned) party ledgers"
      icon={moduleIcon && (IconSystem as any)[moduleIcon]
        ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
        : <IconSystem.list size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
      searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search ledgers...' }}
      viewModeConfig={{ currentMode: viewMode, modes: viewModeOptions, onChange: (m) => setViewMode(m as ViewMode) }}
      filterConfig={{
        onClick: () => setFilterOpen(!filterOpen),
        isActive: filterOpen,
        appliedCount,
      }}
      customToolbarItems={
        <ColumnSelector columns={COLUMN_DEFS} visibleColumns={visibleColumns}
          onApply={applyColumns} onReset={resetColumns} />
      }
      ConfirmDialogPortal={ConfirmDialogPortal}
      trailingContent={
        filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
                <IconSystem.x size={14} />
              </button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <SyutSelect label="Kind"
                  options={[
                    { value: '', label: 'All' },
                    { value: 'GL', label: 'GL (structural)' },
                    { value: 'PARTY', label: 'Party' },
                  ]}
                  value={kindFilter} onChange={(v) => setKindFilter((v as string) || '')}
                  isClearable={false} size="sm" />
              </div>
              <div className="filter-section">
                <SyutSelect label="Type" isAsync loadOptions={loadTypeOptions} defaultOptions cacheOptions={false}
                  value={typeFilter}
                  onChange={(v) => { setTypeFilter((v as string) || ''); setSubtypeFilter(''); }}
                  placeholder="All Types" isClearable size="sm" />
              </div>
              <div className="filter-section">
                <SyutSelect key={`f-sub-${typeFilter}`} label="Subtype" isAsync loadOptions={loadSubtypeOptions}
                  defaultOptions cacheOptions={false} value={subtypeFilter}
                  onChange={(v) => setSubtypeFilter((v as string) || '')}
                  placeholder="All Subtypes" isClearable size="sm" />
              </div>
              <div className="filter-section">
                <SyutSelect label="Nature" isAsync loadOptions={loadNatureOptions} defaultOptions cacheOptions={false}
                  value={natureFilter} onChange={(v) => setNatureFilter((v as string) || '')}
                  placeholder="All Natures" isClearable size="sm" />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Ledger Code</label>
                <input type="text" className="form-input" value={ledgerCodeFilter}
                  onChange={(e) => setLedgerCodeFilter(e.target.value)}
                  placeholder="Filter by code..."
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Ledger Name</label>
                <input type="text" className="form-input" value={ledgerNameFilter}
                  onChange={(e) => setLedgerNameFilter(e.target.value)}
                  placeholder="Filter by name..."
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Currency</label>
                <input type="text" className="form-input" value={currencyFilter} maxLength={3}
                  onChange={(e) => setCurrencyFilter(e.target.value.toUpperCase())}
                  placeholder="e.g., INR"
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.8125rem' }}>
                  <input type="checkbox" checked={includeInactive}
                    onChange={(e) => setIncludeInactive(e.target.checked)} /> Include inactive
                </label>
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={clearFilters}>Clear</button>
              <button className="btn btn-primary" onClick={applyFilters}>Apply</button>
            </div>
          </div>
        )
      }
    >
      <div className="data-grid-container">
        {viewMode === 'list' && (
          <SyutGrid data={data} columns={tableColumns} enableRowSelection
            enablePagination manualPagination totalCount={pagination.totalItems}
            currentPage={(queryParams.page ?? 1) - 1} pageSize={queryParams.pageSize ?? 25}
            onPageChange={handlePageChange} onPageSizeChange={handlePageSizeChange}
            rowActions={getRowActions}
            emptyMessage={loading ? 'Loading...' : 'No ledger accounts found'}
            frozenLeftColumns={['select', 'ledgerCode']} frozenRightColumns={['actions']}
            visibleColumns={[...STRUCTURAL_COLUMN_IDS, ...visibleColumns.filter(id => !STRUCTURAL_COLUMN_IDS.includes(id))]} />
        )}
        {viewMode === 'grid' && (
          <div className="syut-grid-component" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={{
              padding: '1rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
              gap: '1rem', flex: 1, overflow: 'auto', alignContent: 'start',
            }}>
              {data.length === 0 ? (
                <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
                  <IconSystem.list size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
                  <p>{loading ? 'Loading...' : 'No ledger accounts found'}</p>
                </div>
              ) : (
                data.map((l) => (
                  <div key={l.id} style={{
                    background: 'var(--bg-primary)', border: '1px solid var(--border-color)',
                    borderRadius: '0.75rem', padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.5rem',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                      <div>
                        <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)' }}>{l.ledgerCode}</div>
                        <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--text-primary)', margin: '0.25rem 0 0' }}>{l.ledgerName}</h3>
                      </div>
                      <ActionMenu items={getRowActions(l)} />
                    </div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{l.accountTypeName} · {l.subtypeName}</div>
                    <div style={{
                      display: 'flex', gap: '0.75rem', fontSize: '0.75rem', color: 'var(--text-muted)',
                      paddingTop: '0.5rem', borderTop: '1px solid var(--border-color)', flexWrap: 'wrap',
                    }}>
                      <span>{l.ledgerNature}</span>
                      <span>{l.currencyCode}</span>
                      <span className="badge" style={{ marginLeft: 'auto' }}>{l.statusName || 'Active'}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
            {data.length > 0 && (
              <div className="pagination-footer">
                <div className="pagination-info">Showing {data.length} of {pagination.totalItems} items</div>
                <div className="pagination-controls" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <select value={queryParams.pageSize ?? 25}
                    onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                    style={{ padding: '0.375rem 0.5rem', borderRadius: '0.375rem', border: '1px solid var(--border-color)', fontSize: '0.85rem' }}>
                    {[10, 25, 50, 100].map(size => <option key={size} value={size}>{size} / page</option>)}
                  </select>
                </div>
              </div>
            )}
          </div>
        )}
        {viewMode === 'tree' && (
          <div style={{ flex: 1, height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <SyutTreeGrid data={treeNodes} columns={treeColumns} parentKey="parentId"
              showConnectorLines showNodeIcons rowActions={treeRowActions}
              isLoading={treeLoading} isExpandable={isTreeExpandable}
              childrenLoadedIds={childrenLoadedIds} loadingNodeIds={loadingNodeIds}
              onLoadChildren={handleLoadChildren}
              emptyMessage={treeLoading ? 'Loading...' : 'No account types found'} />
          </div>
        )}
      </div>

      <AccountLedgerFormModal isOpen={formModalOpen} mode={formMode} ledger={selectedRecord}
        onSave={handleSaveSuccess}
        onClose={() => { setFormModalOpen(false); setSelectedRecord(null); }}
        openedBy="LedgerAccountsScreen"
        tabId={tabId} moduleName={moduleName}
        onRestoreModal={handleRestoreModal}
        createInstanceId={createInstanceId} copyInstanceId={copyInstanceId}
        minimizeRef={formModalMinimizeRef} />

      <AccountLedgerViewModal isOpen={viewModalOpen} ledger={selectedRecord}
        onClose={() => { setViewModalOpen(false); setSelectedRecord(null); }}
        onEdit={(rec) => handleEdit(rec.id)}
        tabId={tabId} moduleName={moduleName}
        onRestoreView={handleRestoreView} />
    </EnterpriseScreenShell>
  );
};

export default LedgerAccountsScreen;
